import { handleActions } from 'redux-actions'

import { EARLIEST } from 'utils/cateringSchedule'

const SET_CATERING_ADDRESS = 'unicorn/catering_order_details/SET_ADDRESS'
const SET_CATERING_DATE_TIME = 'unicorn/catering_order_details/SET_DATE_TIME'
const SET_CATERING_HEADCOUNT = 'unicorn/catering_order_details/SET_HEADCOUNT'
const SET_CATERING_CONTACT = 'unicorn/catering_order_details/SET_CONTACT'

export const setCateringAddress = (venueId, address) => ({
  type: SET_CATERING_ADDRESS,
  venueId,
  address,
})

export const setCateringDateTime = (venueId, dateTime) => ({
  type: SET_CATERING_DATE_TIME,
  venueId,
  dateTime,
})

export const setCateringHeadcount = (venueId, headcount) => ({
  type: SET_CATERING_HEADCOUNT,
  venueId,
  headcount,
})

// The review page's contact person, sent as `catering.contact` on submit.
export const setCateringContact = (venueId, contact) => ({
  type: SET_CATERING_CONTACT,
  venueId,
  contact,
})

// No address until the customer picks one from the Google suggestions, matching
// corporate-accounts-platform's useCateringRequest: only a picked address has the
// coordinates the delivery-radius check needs.
export const DEFAULT_CATERING_ADDRESS = {
  address: '',
  address2: '',
  suburb: '',
  state: '',
  postcode: '',
  formattedAddress: '',
  lat: null,
  lng: null,
}

// 'earliest' resolves to the soonest available slot each time it's read (see
// utils/cateringSchedule resolveSlot); `time` only applies to a specific date.
export const DEFAULT_CATERING_DATE_TIME = {
  mode: EARLIEST,
  date: null,
  time: '',
}

export const DEFAULT_CATERING_HEADCOUNT = 15

/** Whether the address was picked from the suggestions, so it has coordinates. */
export const hasCateringAddress = (address) =>
  !!address && address.lat != null && address.lng != null

/**
 * One-line label for a catering address, or '' when there's none. With an
 * Address 2 it goes after the street line, e.g. '11 York St, Level 3, Sydney
 * NSW 2000' (same as corporate-accounts-platform); without one it's the picked
 * place's label.
 */
export const cateringAddressLabel = (address) => {
  if (!hasCateringAddress(address)) return ''
  const { address: street, address2, suburb, state, postcode } = address
  if (!address2) return address.formattedAddress || street
  return [street, address2, `${suburb} ${state} ${postcode}`]
    .filter(Boolean)
    .join(', ')
}

// Shared shape for the order module's real deliveryInfo.address
// (pages/order/modules/order.js) — selectInvalidDeliveryInfoByVenueId
// requires address2 non-empty, matching the standard delivery form's own
// required "*Address 2" field. The modal's Address 2 is optional, so a blank
// one (or one persisted before the field existed) still sends a placeholder.
export const toOrderDeliveryAddress = (address) => ({
  address1: address.address,
  address2: (address.address2 || '').trim() || 'N/A',
  suburb: address.suburb,
  state: address.state,
  // The API takes the postcode only as a string, and a persisted request keeps
  // whatever type it was saved with.
  postcode: address.postcode == null ? '' : String(address.postcode),
})

const defaultVenueState = {
  address: DEFAULT_CATERING_ADDRESS,
  dateTime: DEFAULT_CATERING_DATE_TIME,
  headcount: DEFAULT_CATERING_HEADCOUNT,
}

const initialState = {}

export default handleActions(
  {
    [SET_CATERING_ADDRESS]: (state, { venueId, address }) => ({
      ...state,
      [venueId]: {
        ...defaultVenueState,
        ...state[venueId],
        address: { ...DEFAULT_CATERING_ADDRESS, ...address },
      },
    }),
    [SET_CATERING_DATE_TIME]: (state, { venueId, dateTime }) => ({
      ...state,
      [venueId]: {
        ...defaultVenueState,
        ...state[venueId],
        dateTime,
      },
    }),
    [SET_CATERING_HEADCOUNT]: (state, { venueId, headcount }) => ({
      ...state,
      [venueId]: {
        ...defaultVenueState,
        ...state[venueId],
        headcount,
      },
    }),
    [SET_CATERING_CONTACT]: (state, { venueId, contact }) => ({
      ...state,
      [venueId]: {
        ...defaultVenueState,
        ...state[venueId],
        contact,
      },
    }),
  },
  initialState,
)
